import React, { useEffect, useState } from 'react'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  FilterType,
  Input,
  useNotify
} from '@globaleris/ui.library'
import { makeStyles, Typography, useTheme } from '@material-ui/core'
import { Pageview } from '@material-ui/icons'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumEstadoViaje, EnumTipoOperacion } from '../../api'
import { DataTableErrorHandler } from '../../components/errors/scopes/dataTable/dataTableError'
import Page from '../../components/page/page'
import CustomFilter from '../../components/shared/customFilter/customFilter'
import { enumToSelectOptionArray, enumToSelectOptionArrayDefaultValues } from '../../helpers'
import { generateIconsFromConfiguration } from '../../helpers/dataTableActionIconHelper'
import { normalizeFilename } from '../../helpers/fileDownloadHelper'
import { getUserPermissions } from '../../helpers/permissionsHelper'
import { useHttpError, usePageState } from '../../hooks'
import { ActionIcons, DatePeriod, GlobalNotification, NonAuthRoutes } from '../../model'
import { Pages } from '../../model/RBAC/constants'
import {
  getCustomerDomain,
  RootStoreModel,
  storeCustomFilterForCurrentPage,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../redux'
import { default as ShipsModel, default as TravelsModel } from './ships.model'
import { canEditTravels, FetchShips, filterApplicableActionsConfiguration } from './ships.service'
import { styles } from './ships.styles'

export interface ShipsPageProps {}

export const ShipsPage: React.FC<ShipsPageProps> = () => {
  const [t] = useTranslation(['globalerisLibrary', 'ships', 'enums'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  let history = useHistory()
  const { info } = useNotify()

  const { showError, clearError, errorProps } = useHttpError()
  const { dataTableFilter, dataTableSearch, customFilter } = usePageState()
  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: customFilter?.startDate || moment(new Date()).toDate(),
    end: customFilter?.endDate || moment().add(1, 'month').toDate()
  })

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)
  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const shipsTypeFilter = [EnumTipoOperacion.CargaBuque, EnumTipoOperacion.DescargaBuque]

  const [travels, setTravels] = useState<TravelsModel[] | null>(null)
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [firstLoad, setFirstLoad] = useState<boolean>(true)

  useEffect(() => {
    storeCustomFilterForCurrentPage({ startDate: dateRange.start, endDate: dateRange.end })
  }, [])

  useEffect(() => {
    if (!firstLoad) {
      const getTravels = async () => {
        setIsFetching(true)
        setTravels(null)
        const data = await FetchShips(
          customFilter?.startDate ? customFilter?.startDate! : dateRange.start,
          customFilter?.endDate ? customFilter?.endDate! : dateRange.end,
          customFilter?.operationType
        ).then(items => {
          clearError()
          setIsFetching(false)
          return items?.map(item => {
            return completeDataTableActions(item)
          })
        })

        setTravels(data)
      }
      getTravels()
    } else {
      setGlobalNotification(undefined)
      FetchShips(
        customFilter?.startDate ? customFilter?.startDate! : dateRange.start,
        customFilter?.endDate ? customFilter?.endDate! : dateRange.end,
        customFilter?.operationType
      ).then(items => {
        clearError()
        setIsFetching(false)
        setTravels(
          items?.map(item => {
            return completeDataTableActions(item)
          })
        )
      })
      setFirstLoad(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBalanceZone, activeCompany, dateRange])

  const searchFilterInputs: Input[] = [
    {
      name: 'operationType',
      label: t('ships:forms.inputs.shipType'),
      type: DataType.SELECT,
      //required: true,
      selectOptions: enumToSelectOptionArrayDefaultValues(
        EnumTipoOperacion,
        'enums:EnumTipoOperacion',
        shipsTypeFilter
      )
    }
  ]

  const onApplyCustomFilter = async (filterToApply: any) => {
    setDateRange({
      start: filterToApply.startDate,
      end: filterToApply.endDate
    })

    storeCustomFilterForCurrentPage(filterToApply)
  }

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    const totalCount = await countReports(
      filterToApply.startDate,
      filterToApply.endDate,
      filterToApply.operationType
    )
    if (totalCount !== undefined && totalCount < 1000) {
      return true
    }
    return false
  }

  const countReports = async (
    startDate: Date,
    endDate: Date,
    operationType: number
  ): Promise<number | undefined> => {
    const reports = await getReports(startDate, endDate, operationType)
    return reports ? reports.length : undefined
  }

  const getReports = async (
    startDate: Date,
    endDate: Date,
    operationType: number
  ): Promise<TravelsModel[] | null> => {
    return await FetchShips(startDate, endDate, operationType)
  }

  const completeDataTableActions = (item: ShipsModel) => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    }
  }

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'tripDate',
      label: t('ships:dataTable.columns.travelDate'),
      type: DataType.DATE
    },
    {
      name: 'operationType',
      label: t('ships:dataTable.columns.type'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArrayDefaultValues(
        EnumTipoOperacion,
        'enums:EnumTipoOperacion',
        [EnumTipoOperacion.DescargaBuque, EnumTipoOperacion.CargaBuque]
      )
    },
    {
      name: 'code',
      label: t('ships:dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'tsoCode',
      label: t('ships:dataTable.columns.codeTSO'),
      type: DataType.TEXT,
      hideForResponsive: true
    },
    {
      name: 'ship.description',
      label: t('ships:dataTable.columns.shipDescripcion'),
      type: DataType.TEXT,
      hideForResponsive: true
    },
    {
      name: 'ship.capacity',
      label: t('ships:dataTable.columns.shipCapacidad'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.VOLUME
    },
    {
      name: 'originPlant.description',
      label: t('ships:dataTable.columns.originPlantDescripcion'),
      type: DataType.TEXT
    },
    {
      name: 'destinationsIds',
      label: t('ships:dataTable.columns.destinations'),
      type: DataType.CHIPS
    },
    {
      name: 'quantity',
      label: t('ships:dataTable.columns.quantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.VOLUME
    },
    {
      name: 'share',
      label: t('ships:dataTable.columns.share'),
      type: DataType.BOOLEAN,
      hideForResponsive: true
    },
    {
      name: 'owner.description',
      label: t('ships:dataTable.columns.property'),
      type: DataType.TEXT,
      hideForResponsive: true
    },
    {
      name: 'marketersIds',
      label: t('ships:dataTable.columns.marketers'),
      type: DataType.CHIPS,
      hideForResponsive: true
    },
    {
      name: 'soe',
      label: t('ships:dataTable.columns.soe'),
      type: DataType.BOOLEAN,
      hideForResponsive: true
    },
    {
      name: 'tripState',
      label: t('ships:dataTable.columns.state'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumEstadoViaje, 'enums:EnumEstadoViaje')
    }
  ]

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: true,
    filename: normalizeFilename(t('ships:title')),
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: true,
    customInsertEditModal: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('ships:dataTable.analytics')
    },
    onRowEdit: async (row: any) => {
      if (canEditTravels(row)) {
        history.push(NonAuthRoutes.shipsEdit, { id: row.id })
      }

      return true
    },
    onRowInsert: async () => {
      history.push(NonAuthRoutes.shipsAdd)
      return true
    },
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    },
    onRowsDelete: async () => {},
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('ships:dataTable.columns.view'),
        onClick: async (row: any) => {
          if (canEditTravels(row)) {
            history.push(NonAuthRoutes.shipsEdit, { id: row.id })
          }
          return true
        }
      }
    ]
  }

  return (
    <Page notification={globalNotification}>
      <div className={classes.appBar}>
        <Typography variant="h1" color="secondary" className={classes.title}>
          {t('ships:title')}
        </Typography>
        <div className={classes.filters}>
          <div className={classes.customFilter}>
            <CustomFilter
              onApplyFilter={onApplyCustomFilter}
              onPrevalidateFilter={onPrevalidateCustomFilter}
              additionalFilters={searchFilterInputs}
              appliedFilter={customFilter}
              enabled={!isFetching}
            ></CustomFilter>
          </div>
        </div>
      </div>
      <DataTable
        data={travels}
        columns={dataTableColumns}
        options={dataTableOptions}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Ships)}
        appliedFilter={dataTableFilter}
        appliedSearch={dataTableSearch}
      />
    </Page>
  )
}
